import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';
import './About.css';

const principles = [
  {
    title: 'Why Choose Us?',
    introduction: 'Knowledge built on proven experience and real insights. Our guidance is shaped by hands-on market experience and data-driven analysis.',
    items: [
      'Deep Market Knowledge',
      'Personalized Service',
      'Access to Exclusive Properties',
      'Trusted Developer Network',
      'Ethical & Transparent Practices',
      'Seamless End-to-End Support'
    ]
  },
  {
    title: 'Vision',
    introduction: 'To be Dubai’s most trusted and forward-thinking real estate brokerage—recognized for elevating client experiences, delivering value-driven solutions and shaping inspired lifestyles through property.',
    items: []
  },
  {
    title: 'Mission',
    introduction: 'Our mission is to simplify and enrich the property experience by offering:',
    items: [
      'Strategic advice grounded in real-time market intelligence',
      'A handpicked portfolio of premium, off-plan and ready properties',
      'Transparent, ethical and client-first service from start to finish',
      'End-to-end support—from discovery to deal closure'
    ]
  }
];

const team = [
  {
    name: 'Freejo Francis',
    role: 'Founder & CEO',
    image: '/assets/img/team-img-1.jpg',
    quote: 'Every property tells a story. Every investment shapes a future. Our role is to make both extraordinary.',
    paragraphs: [
      'Elevate Realty was founded on a simple conviction that real estate, done right, is one of the most powerful tools for building wealth, security and legacy. Across brokerage, land acquisition and investment advisory, we bring discipline, insight and integrity to every opportunity we pursue on behalf of our clients.',
      'The UAE represents a generational moment in global real estate and we are proud to help our clients participate in it with confidence. Our team is built to think like investors, advise like consultants and serve like partners—combining local expertise with a global outlook to deliver outcomes that stand the test of time.',
      'As we grow, our vision remains unchanged: to be the most trusted name in UAE real estate—elevating not just properties, but the standard of service itself.'
    ]
  },
  {
    name: 'Usman Noor',
    role: 'Founder and Managing Partner',
    image: '/assets/img/team-img-4.jpg',
    quote: 'At Elevate Realty, we’re not just here to sell or lease properties. We are here to raise the standard of what you expect from a real estate experience.',
    paragraphs: [
      'At Elevate Realty, our vision is clear: to create a brokerage built on integrity, transparency and results. With years of hands-on experience in the UAE real estate market, we’ve learned that trust and relationships are the foundation of success, not just transactions.',
      'Whether you’re an investor seeking growth opportunities, a business looking for the perfect commercial space or a family searching for your dream home, our mission is to guide you every step of the way with unmatched expertise and personalized attention.',
      'We believe real estate isn’t just about buildings—it’s about people, purpose and long-term value.',
      'At Elevate Realty, your goals become our goals.'
    ]
  }
];

const statistics = [
  { value: 'AED 600M', label: 'Transacted value' },
  { value: '120+', label: 'Residences placed' },
  { value: '10', label: 'Countries represented' },
  { value: '97%', label: 'Repeat & referred' }
];

const About = () => {
  const [activePrinciple, setActivePrinciple] = useState(0);

  useEffect(() => {
    document.body.classList.add('elevate-home', 'inner-page');
    window.scrollTo({ top: 0, behavior: 'auto' });

    const previousTitle = document.title;
    const descriptionElement = document.head.querySelector('meta[name="description"]');
    const previousDescription = descriptionElement?.getAttribute('content') || '';

    document.title = 'About Elevate Realty';
    descriptionElement?.setAttribute(
      'content',
      'About Elevate Realty - boutique Dubai real estate advisory for landmark residences, private villas and off-plan investments.'
    );

    window.AOS?.init({ duration: 800, once: true, offset: 80 });
    window.AOS?.refreshHard?.();

    return () => {
      document.body.classList.remove('elevate-home', 'inner-page');
      document.title = previousTitle;
      descriptionElement?.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <>
      <Header />

      <main className="inner-main" id="top">
        <section
          className="inner-hero about-hero about-react-hero"
          style={{ '--about-hero-image': 'url("/assets/img/about.jpg")' }}
        >
          <div className="inner-hero__content" data-aos="fade-up">
            <p className="eyebrow">About Elevate</p>
            <h1>Quiet counsel for <em>landmark</em> addresses.</h1>
            <p>
              Elevate Realty is a boutique advisory representing landmark residences,
              private villas and off-plan investments across Dubai’s most sought-after addresses.
            </p>
            <div className="inner-kicker-row">
              <Link className="primary-link" to="/contact-us">Begin a conversation</Link>
              <Link className="outline-link" to="/services">Our services</Link>
            </div>
          </div>
        </section>

        <section className="about-principles-section">
          <div className="about-principles-media" data-aos="fade-right">
            <img src="/assets/img/faq-img-1.jpg" alt="Elevate Realty property advisory" />
          </div>

          <div className="about-principles-content" data-aos="fade-up">
            <p className="eyebrow">Trust and vision</p>
            <h2>Elevating Dubai real estate with trust and vision.</h2>

            <div className="about-accordion">
              {principles.map((principle, index) => {
                const isActive = activePrinciple === index;
                return (
                  <article className={`about-accordion-item ${isActive ? 'is-active' : ''}`} key={principle.title}>
                    <h3>
                      <button
                        type="button"
                        aria-expanded={isActive}
                        aria-controls={`about-principle-${index}`}
                        onClick={() => setActivePrinciple(isActive ? -1 : index)}
                      >
                        <span>{principle.title}</span>
                        <i className={`fa-solid fa-${isActive ? 'minus' : 'plus'}`} aria-hidden="true" />
                      </button>
                    </h3>
                    <div
                      className="about-accordion-panel"
                      id={`about-principle-${index}`}
                      hidden={!isActive}
                    >
                      <p>{principle.introduction}</p>
                      {principle.items.length > 0 && (
                        <ul className="about-feature-list">
                          {principle.items.map((item) => <li key={item}>{item}</li>)}
                        </ul>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section
          className="about-belief-section"
          style={{ '--about-belief-image': 'url("/assets/img/testimonial-bg-3.jpg")' }}
        >
          <div>
            <p className="eyebrow">Our purpose</p>
            <h2>Shaping inspired lifestyles through trust, strategy and service.</h2>
            <p>
              We aim to be your one-stop destination for every real estate need—turning
              dreams into reality and properties into possibilities.
            </p>
          </div>
        </section>

        <section className="inner-section team-section" id="team">
          <div className="team-section__intro">
            <p className="eyebrow">Leadership</p>
            <h2>Our Team</h2>
          </div>

          <div className="team-profiles">
            {team.map((member) => (
              <article className="team-profile" key={member.name}>
                <figure className="team-profile__media">
                  <img src={member.image} alt={member.name} loading="lazy" />
                </figure>
                <div className="team-profile__content">
                  <p className="eyebrow">{member.role}</p>
                  <h3>{member.name}</h3>
                  <blockquote>{member.quote}</blockquote>
                  {member.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="stats" aria-label="Elevate Realty statistics">
          {statistics.map((statistic) => (
            <div key={statistic.label}>
              <strong>{statistic.value}</strong>
              <span>{statistic.label}</span>
            </div>
          ))}
        </section>
      </main>

      <Footer />

      <a
        className="advisor-button"
        href="https://api.whatsapp.com/send?phone=971507135432&text=Hi"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Speak with an advisor on WhatsApp"
      >
        <svg className="advisor-button__icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M21 11.5a8.5 8.5 0 0 1-12.7 7.4L3 20l1.2-5A8.5 8.5 0 1 1 21 11.5Z" />
        </svg>
        <span>Speak with an advisor</span>
      </a>
    </>
  );
};

export default About;
